import { useState } from "react";
import { toast } from "sonner";
import { History, Loader2, Trash2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { deleteChat, listChats } from "@/lib/assistantApi";
import { fmtAgo } from "@/lib/studioApi";
import { safeApiError } from "@/lib/safeErrorText";

/** Saved conversations — open one to continue it, or delete it. */
export default function PastChats({ currentId, onOpen, onDeleted }) {
  const [chats, setChats] = useState(null);
  const load = () => { listChats().then(setChats).catch(() => setChats((c) => c || [])); };
  const remove = async (id) => {
    try {
      await deleteChat(id);
      setChats((c) => (c || []).filter((x) => x.id !== id));
      if (id === currentId) onDeleted();
    } catch (e) { toast.error("Couldn't delete that chat", { description: safeApiError(e) }); }
  };
  return (
    <Popover onOpenChange={(o) => o && load()}>
      <PopoverTrigger asChild>
        <button type="button" data-testid="assistant-history-btn" className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-white/70 hover:bg-white/[0.08] hover:text-white">
          <History className="h-3.5 w-3.5" /> Past chats
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="z-[80] w-72 border-white/10 bg-[#0a0d1c] p-2 text-white">
        {chats === null ? <p className="flex items-center gap-2 p-2 text-[12px] text-white/55"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…</p>
          : !chats.length ? <p data-testid="assistant-history-empty" className="p-2 text-[12px] text-white/55">No saved chats yet.</p>
            : (
              <ul data-testid="assistant-history-list" className="max-h-72 space-y-1 overflow-y-auto">
                {chats.map((c) => (
                  <li key={c.id} className={`group flex items-center gap-1 rounded-lg ${c.id === currentId ? "bg-white/[0.08]" : "hover:bg-white/[0.05]"}`}>
                    <button type="button" data-testid={`assistant-history-${c.id}`} onClick={() => onOpen(c.id)} className="min-w-0 flex-1 px-2.5 py-2 text-left">
                      <span className="block truncate text-[13px] text-white/85">{c.title || "Chat"}</span>
                      <span className="font-mono text-[9px] uppercase tracking-wider text-white/40">{fmtAgo(c.updated_at)}</span>
                    </button>
                    <button type="button" data-testid={`assistant-history-delete-${c.id}`} onClick={() => remove(c.id)} aria-label="Delete chat" className="mr-1 grid h-7 w-7 place-items-center rounded-full text-white/35 hover:bg-white/[0.08] hover:text-rose-300">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
      </PopoverContent>
    </Popover>
  );
}
